/**
 * Adding up ad numbers, and the rates and changes worked out from them
 * (docs/ad-analytics.spec.md, "The numbers").
 *
 * Two rules live here so they can't be broken at a call site:
 *
 * - **Rates come from totals.** A period's click-through rate is its clicks over
 *   its impressions, never an average of daily or per-campaign rates. Every
 *   rate is therefore a function of an {@link AdTotals}, and nothing else.
 * - **Currencies are never mixed.** Totals are kept per currency, and adding two
 *   in different currencies throws rather than producing a number in neither.
 */

/** One currency's totals. Spend is in micros (millionths of the currency's unit), as Google reports it. */
export interface AdTotals {
  currencyCode: string
  spendMicros: number
  impressions: number
  clicks: number
  /** Google's "Conversions" column: fractional, because Google can split one between campaigns. */
  conversions: number
}

/** How a number moved from the period before. */
export type AdChange =
  | { kind: 'change'; amount: number; percent: number }
  /** It was zero (or nothing) before and isn't now: there is no percentage to give. */
  | { kind: 'new'; amount: number }
  /** Nothing to show: zero or nothing on both sides, or nothing now. */
  | { kind: 'none' }

export function emptyAdTotals(currencyCode: string): AdTotals {
  return { currencyCode, spendMicros: 0, impressions: 0, clicks: 0, conversions: 0 }
}

/** @throws Error when the two are in different currencies. */
export function addAdTotals(a: AdTotals, b: AdTotals): AdTotals {
  if (a.currencyCode !== b.currencyCode) {
    throw new Error(`Can't add ${a.currencyCode} to ${b.currencyCode}`)
  }
  return {
    currencyCode: a.currencyCode,
    spendMicros: a.spendMicros + b.spendMicros,
    impressions: a.impressions + b.impressions,
    clicks: a.clicks + b.clicks,
    conversions: a.conversions + b.conversions,
  }
}

function ratio(top: number, bottom: number): number | null {
  return bottom === 0 ? null : top / bottom
}

/** Clicks over impressions, or null with no impressions to divide by. */
export function clickThroughRate(totals: AdTotals): number | null {
  return ratio(totals.clicks, totals.impressions)
}

/** Spend over clicks, in micros, or null with no clicks. */
export function costPerClickMicros(totals: AdTotals): number | null {
  return ratio(totals.spendMicros, totals.clicks)
}

/** Spend over conversions, in micros, or null with no conversions. */
export function costPerConversionMicros(totals: AdTotals): number | null {
  return ratio(totals.spendMicros, totals.conversions)
}

/**
 * How `current` moved from `earlier`. A rate with nothing to divide by is null,
 * and counts as nothing on its side.
 *
 * - Zero or nothing on both sides, or nothing now: `none` (shown as a dash).
 * - Zero or nothing before, something now: `new`, with the amount.
 * - Otherwise the amount and the fraction it moved by (0.15 is 15%).
 */
export function adChange(current: number | null, earlier: number | null): AdChange {
  if (current === null) return { kind: 'none' }
  if (earlier === null || earlier === 0) {
    return current === 0 ? { kind: 'none' } : { kind: 'new', amount: current }
  }
  const amount = current - earlier
  return { kind: 'change', amount, percent: amount / earlier }
}
